import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Apptentive from 'helpers/apptentive'
import SearchIcon from '@mui/icons-material/Search'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import ErrorMessage from 'components/ErrorMessage'

import { screenSize } from 'styles/shared'

import { color } from 'styles/colors'

type Props = {
  onChange: (value: string, event: React.ChangeEvent<HTMLInputElement> | null) => void;
  onSearch: (value: string) => void;
  noForm?: boolean;
  ariaLabel: string;
  value?: string;
  placeholder?: string | undefined;
  helperText?: string | null;
  className?: string;
  showClearIcon?: boolean;
  focusOnMount?: boolean;
  variant?: string;
  hideSearchButton?: boolean;
  errorMessage?: string;
  onClickClearIcon?: () => void;
  showSpinner?: boolean;
}

const Search = forwardRef<HTMLInputElement, Props>(({
  onChange,
  onSearch,
  noForm,
  ariaLabel,
  value,
  placeholder,
  helperText,
  focusOnMount,
  className,
  variant,
  showClearIcon,
  onClickClearIcon,
  hideSearchButton,
  errorMessage,
  showSpinner = false,
  ...inputProps
}: Props, ref) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (focusOnMount) inputRef?.current?.focus()
  }, [focusOnMount])

  const clear = useCallback(() => {
    if (onClickClearIcon) {
      onClickClearIcon()
    } else { // TODO: should we always call onChange and onSearch even if we have onClickClearIcon?
      onChange('', null)
      onSearch('')
    }
    inputRef.current?.focus()
  }, [onChange, onSearch, onClickClearIcon])

  const onKeyUp = useCallback((event) => {
    if (event.key === 'Escape') {
      clear()
    }
  }, [clear])

  const onClickSubmit = useCallback((event) => {
    event.preventDefault()
    Apptentive.sendEvent('search_from_' + ariaLabel.replace(' ', '_').toLowerCase())
    onSearch(value || '')
  }, [onSearch, value, ariaLabel])
  const isSearchEnabled = !showSpinner && value
  const renderSearchInput = () => {
    return (
      <>
        <S.SearchContainer>
          <S.Input
          ref={ref || inputRef}
          type="text"
          placeholder={placeholder}
          aria-label="search"
          onChange={(event) => onChange(event.target.value, event)}
          styleVariant={variant}
          hideSearchButton={hideSearchButton}
          {...{ onKeyUp, value, ...inputProps }}
          />
          {(value || showClearIcon) && (
          <S.Clear
            role="button"
            aria-label="clear"
            className="icon-cancel-circle"
            onClick={clear}
            type="button"
          />
          )}

          {!hideSearchButton && (
            variant === 'text'
              ? (
                <Button
                decoration="positive"
                enabled={!!isSearchEnabled}
                styles={{
                  button: {
                    height: 39,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 12,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 12
                  }
                }}
                onPress={onClickSubmit}
                showSpinner={showSpinner}
                {...{ text: 'Search' }}
                />)
              : (
                <Button
                showSpinner={showSpinner}
                decoration="positive"
                enabled={!!isSearchEnabled}
                styles={{
                  button: {
                    height: 39,
                    minWidth: 25,
                    paddingRight: 8,
                    paddingLeft: 8,
                    borderWidth: 1,
                    borderLeftWidth: 0,
                    borderColor: color('border'),
                    backgroundColor: color('iconBackground'),
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 12,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 12
                  }
                }}
                onPress={onClickSubmit}
                >
                  <S.SearchIcon />
                </Button>
              )
          )
        }
        </S.SearchContainer>

        {helperText && <S.HelperText>{helperText}</S.HelperText>}
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      </>
    )
  }

  if (noForm) {
    return renderSearchInput()
  } else {
    return (
      <form aria-label={ariaLabel} role="search" {...{ className }} onSubmit={onClickSubmit}>
        {renderSearchInput()}
      </form>
    )
  }
})

export default Search

const buttonWidth = (variant: string | undefined) => variant === 'icon' ? '39px' : '68px'

const S = {
  SearchContainer: styled.div`
    display: flex;
  `,
  Input: styled.input.withConfig({
    shouldForwardProp: (prop) => !['hideSearchButton', 'styleVariant'].includes(prop)
  })<{ styleVariant: string | undefined, hideSearchButton: boolean | undefined }>`
    height: 39px;
    width: calc(100% - ${({ styleVariant, hideSearchButton }) => hideSearchButton ? '0px' : buttonWidth(styleVariant)});
    display: inline-block;
    padding-right: 33px;

    font-size: 12pt;
    box-shadow: none;
    border: 1px solid ${color('border')};
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    border-right: none;

    &:placeholder {
      color: ${color('searchPlaceholder')};
    }
    
    &:focus {
      border: 1px solid ${color('border')} !important;
      border-right: none !important;
      box-shadow: unset !important;
    }

     @media ${screenSize.medium} {
      width: 100%;
    }

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  Clear: styled.button`
    color: ${color('clearIcon')};
    margin: auto 2px auto -34px;
    padding: 9px;
    height: 32px;
    width: 32px;

    &:hover {
      color: ${color('clearIconHover')};
    }
  `,
  HelperText: styled.div`
    margin-top: 3px;
    @media ${screenSize.small} {
      margin: 10px 20px;
      text-align: center;
    }
  `,
  SearchIcon: styled(SearchIcon)`
    color: ${color('pageTitle')};
    font-size: 23px;
  `
}
